// Build a markdown string (frontmatter + body) from the editor form state.
// Mirrors the parser in `frontmatter.ts` - keys must round-trip cleanly.

import type { Sop } from "./types";

function quote(s: string): string {
  // Always quote strings; escape internal double quotes. Simple + safe.
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function inlineList(items: string[]): string {
  if (items.length === 0) return "[]";
  return `[${items.map((s) => quote(s)).join(", ")}]`;
}

export interface EditorState {
  slug: string;
  title: string;
  category: string;
  type: "sop" | "cheat-sheet";
  status: string;
  description: string;
  owner: string;
  created: string;
  updated: string;
  estimatedMinutes?: number;
  tags: string[];
  related: string[];
  video: string;
  docs: { title: string; url: string }[];
  body: string;
}

export function fromSop(sop: Sop): EditorState {
  return {
    slug: sop.slug,
    title: sop.title,
    category: sop.category,
    type: sop.type,
    status: sop.status,
    description: sop.description ?? "",
    owner: sop.owner,
    created: sop.created,
    updated: sop.updated,
    estimatedMinutes: sop.estimatedMinutes,
    tags: [...sop.tags],
    related: [...sop.related],
    video: sop.video ?? "",
    docs: sop.docs.map((d) => ({ ...d })),
    body: sop.body,
  };
}

export function emptyEditorState(): EditorState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    slug: "",
    title: "",
    category: "onboarding",
    type: "sop",
    status: "draft",
    description: "",
    owner: "team",
    created: today,
    updated: today,
    estimatedMinutes: undefined,
    tags: [],
    related: [],
    video: "",
    docs: [],
    body: "",
  };
}

export function serialize(state: EditorState): string {
  const lines: string[] = ["---"];
  lines.push(`title: ${quote(state.title)}`);
  lines.push(`slug: ${state.slug}`);
  lines.push(`category: ${state.category}`);
  if (state.type === "cheat-sheet") lines.push(`type: cheat-sheet`);
  lines.push(`tags: ${inlineList(state.tags)}`);
  lines.push(`owner: ${state.owner}`);
  if (state.created) lines.push(`created: ${state.created}`);
  if (state.updated) lines.push(`updated: ${state.updated}`);
  if (typeof state.estimatedMinutes === "number" && !isNaN(state.estimatedMinutes)) {
    lines.push(`estimated_minutes: ${state.estimatedMinutes}`);
  }
  if (state.description) lines.push(`description: ${quote(state.description)}`);
  if (state.video) lines.push(`video: ${quote(state.video)}`);
  if (state.docs.length > 0) {
    lines.push(`docs:`);
    for (const d of state.docs) {
      lines.push(`  - title: ${quote(d.title)}`);
      lines.push(`    url: ${quote(d.url)}`);
    }
  }
  if (state.related.length > 0) lines.push(`related: ${inlineList(state.related)}`);
  lines.push(`status: ${state.status}`);
  lines.push("---");
  lines.push("");
  // Trim trailing whitespace on body so we don't accumulate blank lines on
  // every save round-trip.
  lines.push(state.body.replace(/\s+$/g, ""));
  lines.push("");
  return lines.join("\n");
}
